import moment from 'moment';
import ModelFactory from '../model-factory';
import {
  AccessType, BaseModel, UserType, Location, Organization, Position,
} from '../models';
import PermissionsHandler from './permissions-handler';

export const PHONE_TYPES = ['mobilePhone', 'phoneNumber', 'firstAlternativePhone', 'secondAlternativePhone'];

const IDSAS = 'ED90983754E201DE9CAC001CC416E56E';

function removeBlankSpacesText(text) {
  return text.toString().replace(/\s+/g, '') || null;
}

/**
 * format example at end of file
 */
export default class User extends BaseModel {
  constructor(opts = {}) {
    super(opts);

    this.userid = opts.userid || null;
    this.firstName = opts.first_name || null;
    this.middleName = opts.middle_name || null;
    this.lastName = opts.last_name || null;
    // eslint-disable-next-line max-len
    this.agesconPhone = opts.ztelefono_agescon ? removeBlankSpacesText(opts.ztelefono_agescon) : '';
    this.mobilePhone = opts.phone_number ? removeBlankSpacesText(opts.phone_number) : null;
    this.phoneNumber = opts.alt_phone || null;
    this.firstAlternativePhone = opts.fax_phone || null;
    this.secondAlternativePhone = opts.beeper_phone || null;
    this.telephonePreferente = opts.ztelefono_preferente || null;
    this.isWhatsappAllowed = opts.zpermitir_whatsapp ? opts.zpermitir_whatsapp['@id'] : null;
    this.zfechaIniContrato = null;
    if (opts.zfecha_ini_contrato) {
      this.zfechaIniContrato = moment.unix(opts.zfecha_ini_contrato).format('DD/MM/YYYY');
    }
    this.zfechaFinContrato = null;
    if (opts.zfecha_fin_contrato) {
      this.zfechaFinContrato = moment.unix(opts.zfecha_fin_contrato).format('DD/MM/YYYY');
    }
    // TODO: eliminar
    this.preferredPhoneKey = this.setPreferredPhone(opts.ztelefono_preferente) || PHONE_TYPES[0];

    this.dni = opts.zdni || null;
    this.emailAddress = opts.email_address || null;

    this.versionNumber = opts.version_number || null;
    this.roomLocation = opts.room_location || null;

    this.location = null;
    if (opts.location != null) {
      this.location = new Location(opts.location);
    }

    this.organization = null;
    if (opts.organization != null) {
      this.organization = new Organization(opts.organization);
    }

    this.admin_organization_id = null;
    this.admin_organization_name = null;
    if (opts.admin_org != null) {
      this.admin_organization_id = opts.admin_org['@id'];
      this.admin_organization_name = opts.admin_org['@COMMON_NAME'];
    }

    this.accessType = null;
    this.accessTypeTic = false;
    if (opts.access_type != null) {
      this.accessType = new AccessType(opts.access_type);
    }

    this.userType = null;
    if (opts.type != null) {
      this.userType = new UserType(opts.type);
    }

    this.position = null;
    if (opts.position != null) {
      this.position = new Position(opts.position);
    }

    this.KdGroupMember = opts.kd_group_member || false;
    this.ticGroupMember = opts.tic_group_member || false;
    this.micsPilotoPulso = opts.mics_piloto_pulso || false;
    this.formsMigrationGroupMember = opts.forms_migration_group_member || false;
    this.whatsappGroupMember = opts.whatsapp_group_member || false;

    this.permissions = new PermissionsHandler(opts.permissions_role);
    this.zusuarioDmsas = opts.zusuario_dmsas || null;
    this.gerhonteloc = null;
    if (opts.gerhonteloc != null) {
      this.gerhonteloc = new Location(opts.gerhonteloc);
    }
    this.sala = new BaseModel(opts.zid_rel_planta_sala) || null;
    this.edificio = new BaseModel(opts.zid_rel_loc_edificio) || null;
    this.planta = new BaseModel(opts.zid_rel_loc_planta) || null;
    this.isUserTecnical = this.hasRole('accen_ui_key_user', 'View');
    this.isProfilelIT = opts.isProfilelIT || false;
    this.accessTypeTic = this.hasRole('accen_ui_key_user', 'View');

    if (opts.fechaPassCaduca && opts.fechaPassCaduca.fechaCaducidadContrasena) {
      this.fechaPassCaduca = opts.fechaPassCaduca.fechaCaducidadContrasena || null;
    }

    this.cuotaCorreo = opts.cuotaCorreo || 0;
    this.gruposDmesas = opts.gruposDmesas || '';

    if (opts.company && opts.company['@id']) {
      const idCompanyUser = opts.company['@id'];
      const idCompanyUserSub = idCompanyUser.substring(2, idCompanyUser.length - 1);
      this.companyId = idCompanyUserSub || null;
      this.companyIsSAS = idCompanyUserSub === IDSAS;
    } else {
      this.companyId = null;
      this.companyIsSAS = false;
    }
    this.provinceUser = opts.provinceUser || null;
  }

  setPreferredPhone(phoneNumber) {
    let key = null;
    if (phoneNumber != null) {
      key = PHONE_TYPES
        .find((phoneKey) => this[phoneKey] === phoneNumber);
    }
    return key;
  }

  get preferredPhone() {
    return this[this.preferredPhoneKey];
  }

  get fullName() {
    return `${this.middleName || ''} ${this.lastName || ''} ${ this.firstName || ''}`;
  }

  hasIosUser() {
    return this.accessType.id === '400312';
  }

  hasRole(funcAccess, accessLevel) {
    return this.permissions.hasAuth(funcAccess, accessLevel);
  }

  // eslint-disable-next-line class-methods-use-this
  addRoles(opts) {
    const { permissionsRole } = opts;
    const roles = [];
    for (let x = 0; x < permissionsRole.length - 1; x += 1) {
      roles.push(permissionsRole[x].func_access['@REL_ATTR']);
    }
    return roles;
  }
}

export const modelFactory = new ModelFactory('cnt', (data) => new User(data));

/*
  format:
  @COMMON_NAME: "Keyuser, User Pruebas"
  @REL_ATTR: "U'507CDEB1220801F8A8A1000C29CEC9CF'"
  @id: "U'507CDEB1220801F8A8A1000C29CEC9CF'"
  KD_APPROVAL: {@TYPE: "BREL", link: {…}}
  access_type: { ... }
  all_chg: {@TYPE: "BREL", link: {…}}
  all_creq: {@TYPE: "BREL", link: {…}}
  all_iss: {@TYPE: "BREL", link: {…}}
  all_open_creq: {@TYPE: "QREL", link: {…}}
  att_ntfrlist: {@TYPE: "BREL", link: {…}}
  beeper_phone: 222222222222222
  cenv: {@TYPE: "BREL", link: {…}}
  chg_nf_list: {@TYPE: "BREL", link: {…}}
  cntchgntf: {@TYPE: "BREL", link: {…}}
  cnthandling_list: {@TYPE: "BREL", link: {…}}
  cntissntf: {@TYPE: "BREL", link: {…}}
  cntntf: {@TYPE: "BREL", link: {…}}
  combo_name: "Keyuser, User Pruebas"
  combo_name2: "Keyuser,User,Pruebas"
  confirm_save: 0
  contact_num: "keyuser01"
  cr_nf_list: {@TYPE: "BREL", link: {…}}
  creation_date: 1520427974
  creation_user: "srvcdesk"
  delete_flag: {@id: "4551", @REL_ATTR: "0", @COMMON_NAME: "Active", link: {…}}
  email_address: "jaresurreccion@emergya.com"
  fax_phone: 111111
  first_name: "User"
  group_list: {@TYPE: "BREL", link: {…}}
  integration_cnt: 0
  iss_nf_list: {@TYPE: "BREL", link: {…}}
  last_mod_by: "keyuser"
  last_mod_dt: 1594366642
  last_name: "Keyuser"
  link: { ... }
  linked_id_usp_contact: "U'507CDEB1220801F8A8A1000C29CEC9CF'"
  location: { ... }
  log_reader: {@TYPE: "BREL", link: {…}}
  macro_ntf: {@TYPE: "BREL", link: {…}}
  member_list: {@TYPE: "BREL", link: {…}}
  mgs_ntf: {@TYPE: "BREL", link: {…}}
  middle_name: "Pruebas"
  notify_method1: {@id: "1800", @REL_ATTR: "1800", @COMMON_NAME: "Email", link: {…}}
  notify_urgency1: {@id: "400", @REL_ATTR: "1", @COMMON_NAME: "Low", link: {…}}
  notify_urgency2: {@id: "401", @REL_ATTR: "2", @COMMON_NAME: "Normal", link: {…}}
  notify_urgency3: {@id: "402", @REL_ATTR: "3", @COMMON_NAME: "High", link: {…}}
  notify_urgency4: {@id: "403", @REL_ATTR: "4", @COMMON_NAME: "Emergency", link: {…}}
  open_tickets: {@TYPE: "QREL", link: {…}}
  organization: { ... }
  persistent_id: "cnt:507CDEB1220801F8A8A1000C29CEC9CF"
  pgroups: "(0)"
  phone_number: 222223234
  producer_id: "cnt"
  roles: {@TYPE: "BREL", link: {…}}
  room_location: "ALMONTE"
  service_chgcat: {@TYPE: "BREL", link: {…}}
  service_isscat: {@TYPE: "BREL", link: {…}}
  service_locs: {@TYPE: "BREL", link: {…}}
  service_pcat: {@TYPE: "BREL", link: {…}}
  service_wftpl: {@TYPE: "BREL", link: {…}}
  type: { ...}
  userid: "keyuser"
  version_number: 460
  web_url: " <url> "
  workload: {@TYPE: "QREL", link: {…}}
  workload_chg: {@TYPE: "QREL", link: {…}}
  workload_crwf: {@TYPE: "QREL", link: {…}}
  workload_iss: {@TYPE: "QREL", link: {…}}
  workload_isswf: {@TYPE: "QREL", link: {…}}
  workload_wf: {@TYPE: "QREL", link: {…}}
  zdni: "84744506V"
  ztelefono_preferente: 111111
*/
